import styled from "styled-components"
import { useState, useEffect } from "react"

// Styled components for the AI answer field
const StyledContainer = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`

const AnswerHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`

const AIAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`

const AIIcon = styled.div`
  color: white;
  font-size: 18px;
  font-weight: bold;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

const AITitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
`

const Timestamp = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
`

const AnswerContent = styled.div`
  color: #1f2937;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
  
  p {
    margin-bottom: 1rem;
  }
  
  code {
    background-color: #e5e7eb;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.9rem;
  }
  
  pre {
    background-color: #1f2937;
    color: #f9fafb;
    padding: 1rem;
    border-radius: 6px;
    overflow-x: auto;
    margin: 1rem 0;
  }

  .loading-dots {
    display: inline-flex;
    align-items: center;
  }
  
  .loading-dots span {
    animation: dotPulse 1.5s infinite ease-in-out;
    margin-left: 2px;
  }
  
  .loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }
  
  .loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }
  
  .cursor {
    animation: blink 1s infinite;
  }

  @keyframes dotPulse {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`

const TypewriterText = ({ text, speed = 10 }) => {
  const [displayedText, setDisplayedText] = useState("")
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex])
        setCurrentIndex((prev) => prev + 1)
      }, speed)

      return () => clearTimeout(timer)
    } else {
      setIsComplete(true)
    }
  }, [currentIndex, text, speed])

  return (
    <AnswerContent>
      {displayedText}
      {!isComplete && <span className="cursor">|</span>}
    </AnswerContent>
  )
}

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  gap: 0.5rem;
`

const ActionButton = styled.button`
  background-color: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background-color: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`

export function WegetAnswerField({ answer, question, timestamp = new Date(), typewriter = true, loading = false }) {
  return (
    <>
      <StyledContainer>
        <AnswerHeader>
          <AIAvatar>
            <AIIcon>AI</AIIcon>
          </AIAvatar>
          <HeaderText>
            <AITitle>AI 응답</AITitle>
            <Timestamp>{timestamp.toLocaleTimeString()}</Timestamp>
          </HeaderText>
        </AnswerHeader>

        {question &&
          <>
            <AnswerContent>
              질문:
              "{question}"
            </AnswerContent>
            <br />
          </>
        }
        {loading ? (
          <AnswerContent>
            <div className="loading-dots">
              생각 중<span>.</span>
              <span>.</span>
              <span>.</span>
            </div>
          </AnswerContent>
        ) : typewriter ? (
          <TypewriterText text={answer} />
        ) : (
          <AnswerContent>{answer}</AnswerContent>
        )}

        {/* <ActionButtons>
                <ActionButton>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                    </svg>
                    복사
                </ActionButton>
                <ActionButton>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                    </svg>
                    공유
                </ActionButton>
            </ActionButtons> */}
      </StyledContainer>
    </>
  )
}